import PropTypes from 'prop-types';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';

// Components
import LinkBase from '@/components/base/LinkBase';
import Icon from '@/components/base/Icon';
import NotificationsCounter from '@/components/base/NotificationsCounter';

// Utils
import { INSTAGRAM } from '@/lib/socialProfile';
import { getTabsList } from '@/lib/manage/tabs';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useMentions from '@/utils/hooks/swr/useMentions';
import useComments from '@/utils/hooks/swr/useComments';
import useAcl from '@/hooks/user/useAcl';
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';
import useUser from '@/utils/hooks/swr/useUser';

const refreshInterval = 10000;

const ConversationsTabs = ({ unreadComments, unreadMentions }) => {
    const { t } = useTranslation('conversations');
    const router = useRouter();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { hasAcl } = useAcl();
    const tabs = getTabsList(t);
    const { user } = useUser();
    const { formatNumber } = useFormatNumber(user?.settings?.numbers_format);
    const socialNetworkType = activeSocialProfile?.social_network_type ?? INSTAGRAM;

    const hasAccess = (item) => {
        if (item?.permissions) {
            return hasAcl(item.permissions);
        }

        return true;
    };

    const { unreadCount } = useComments({
        activeSPID: activeSocialProfile.id,
        limit: 1,
        options: { refreshInterval: refreshInterval },
        shouldFetch: hasAccess(tabs[socialNetworkType][0]) && undefined === unreadComments
    });
    const { unreadCount: unReadCountMentions } = useMentions({
        activeSPID: activeSocialProfile.id,
        limit: 1,
        options: { refreshInterval: refreshInterval },
        shouldFetch:
            hasAccess(tabs[socialNetworkType][1]) &&
            undefined === unreadMentions &&
            socialNetworkType === INSTAGRAM
    });

    const counts = {};

    if (INSTAGRAM === socialNetworkType) {
        counts[INSTAGRAM] = [
            undefined !== unreadComments ? unreadComments : unreadCount,
            undefined !== unreadMentions ? unreadMentions : unReadCountMentions
        ];
    }

    return (
        <div
            role="tablist"
            className="tab-list flex items-center space-x-9"
            data-testid="conversations-tabs">
            {tabs[socialNetworkType]?.map((tab, index) => {
                if (hasAccess(tab)) {
                    return (
                        <LinkBase
                            key={index}
                            href={tab.url}
                            className="relative flex items-center font-GBold text-3.5xl">
                            <span
                                className={clsx('whitespace-nowrap', {
                                    'opacity-30': router?.route !== tab.url
                                })}>
                                <Icon
                                    className={clsx('mr-3 text-xl', {
                                        'text-icoBlue': router?.route === tab.url
                                    })}
                                    icon={tab.icon}
                                />
                                {tab.name}
                            </span>
                            {typeof counts[socialNetworkType]?.[index] === 'number' &&
                            counts[socialNetworkType]?.[index] > 0 ? (
                                <NotificationsCounter className="absolute -right-5 -top-2.5 flex justify-center md:leading-[13px]">
                                    {formatNumber(counts[socialNetworkType]?.[index])}
                                </NotificationsCounter>
                            ) : null}
                        </LinkBase>
                    );
                }
            })}
        </div>
    );
};

ConversationsTabs.propTypes = {
    unreadComments: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    unreadMentions: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    unreadDM: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default ConversationsTabs;
